import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

// --- TYPE DEFINITIONS ---
type Point = { x: number; y: number };
type Tool = 'freehand' | 'points' | 'pan';
interface PathState {
  path: Point[];
  controlPoints: Point[];
}

// --- PROPS INTERFACE ---
interface GumLineEditorProps {
  imageUrl: string;
  onConfirm: (path: { x: number; y: number }[]) => void;
  onCancel: () => void;
  initialPath: { x: number; y: number }[] | null;
}

// --- INLINE ICON DEFINITIONS ---
const XIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const RefreshCwIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M3 2v6h6" /><path d="M21 12A9 9 0 0 0 6 5.3L3 8" /><path d="M21 22v-6h-6" /><path d="M3 12a9 9 0 0 0 15 6.7l3-2.7" /></svg>
);
const CheckCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>
);
const ZoomInIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
);
const ZoomOutIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
);
const UndoIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" /></svg>
);
const RedoIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3-2.3" /></svg>
);
const HandIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" /><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" /><path d="M10 9V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v5" /><path d="M6 14v-1a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1.5" /><path d="M18 18.5a1.5 1.5 0 0 1-3 0V12a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v1.5" /><path d="M22 14h-2.5a1.5 1.5 0 0 0-1.5 1.5V22" /></svg>
);
const SplineIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M4 12c0-4.4 3.6-8 8-8s8 3.6 8 8" /><circle cx="4" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="20" cy="12" r="2" /></svg>
);
const PenToolIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="m12 19 7-7 3 3-7 7-3-3z" /><path d="m18 13-1.5-7.5-7.5-1.5L13.5 18.5l1.5-7.5-7.5-1.5" /></svg>
);
const FitToScreenIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M15 3h6v6" /><path d="M9 3H3v6" /><path d="M15 21h6v-6" /><path d="M9 21H3v-6" /></svg>
);

// --- UI HELPER COMPONENTS ---
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="relative group flex items-center">
    {children}
    <div className="absolute left-full ml-3 px-2 py-1 bg-zinc-800 text-white text-xs font-semibold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
      {text}
    </div>
  </div>
);

const PanelSection: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center py-2 text-left">
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-400">{title}</h3>
      </button>
      {isOpen && <div className="py-3 space-y-3">{children}</div>}
    </div>
  );
};

const ControlSlider: React.FC<{ label: string; value: number; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; min: number; max: number; step?: number; onDoubleClick?: () => void; }> = ({ label, value, onChange, min, max, step = 1, onDoubleClick }) => (
  <div>
    <div className="flex justify-between items-center text-xs mb-1">
      <label onDoubleClick={onDoubleClick} title="Double-click to reset" className="font-medium text-zinc-300 cursor-pointer">{label}</label>
      <span className="font-mono text-zinc-400">{value.toFixed(1)}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={onChange} className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer range-slider" />
  </div>
);

const GumLineEditor: React.FC<GumLineEditorProps> = ({ imageUrl, onConfirm, onCancel, initialPath }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(new Image());
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const previousToolRef = useRef<Tool>('freehand');

  const [currentState, setCurrentState] = useState<PathState>({ path: [], controlPoints: [] });
  const [history, setHistory] = useState<PathState[]>([currentState]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const [isDrawing, setIsDrawing] = useState(false);
  const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
  const [activeTool, setActiveTool] = useState<Tool>('freehand');
  const [draggingPoint, setDraggingPoint] = useState<{ type: 'anchor' | 'control', index: number } | null>(null);
  const [lineWidth, setLineWidth] = useState(4);
  const [startPanPoint, setStartPanPoint] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);

  const finalActiveTool = isSpacePressed && activeTool !== 'pan' ? 'pan' : activeTool;

  const updateCurrentState = useCallback((newState: PathState | ((prevState: PathState) => PathState)) => {
    const nextState = typeof newState === 'function' ? newState(currentState) : newState;
    setCurrentState(nextState);
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(nextState);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [currentState, history, historyIndex]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentState(history[newIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCurrentState(history[newIndex]);
    }
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image.src || image.naturalWidth === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { path, controlPoints } = currentState;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(viewState.x, viewState.y);
    ctx.scale(viewState.scale, viewState.scale);
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);

    ctx.strokeStyle = '#008000';
    ctx.lineWidth = lineWidth / viewState.scale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = 10 / viewState.scale;

    if (activeTool === 'freehand' && path.length > 1) {
      ctx.beginPath(); ctx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
      ctx.stroke();
    } else if (activeTool === 'points' && path.length > 0) {
      if (path.length > 1) {
        ctx.beginPath(); ctx.moveTo(path[0].x, path[0].y);
        for (let i = 0; i < path.length - 1; i++) {
          const control = controlPoints[i] || { x: (path[i].x + path[i + 1].x) / 2, y: (path[i].y + path[i + 1].y) / 2 };
          ctx.quadraticCurveTo(control.x, control.y, path[i + 1].x, path[i + 1].y);
        }
        ctx.stroke();
      }
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'; ctx.lineWidth = 1 / viewState.scale;
      ctx.setLineDash([4 / viewState.scale, 4 / viewState.scale]); ctx.shadowColor = 'transparent';
      controlPoints.forEach((cp, i) => {
        if (path[i] && path[i + 1]) {
          ctx.beginPath(); ctx.moveTo(path[i].x, path[i].y); ctx.lineTo(cp.x, cp.y); ctx.lineTo(path[i + 1].x, path[i + 1].y); ctx.stroke();
        }
      });
      ctx.restore();
      path.forEach(p => {
        ctx.fillStyle = '#008000'; ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1.5 / viewState.scale;
        ctx.beginPath(); ctx.arc(p.x, p.y, 5 / viewState.scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      });
      controlPoints.forEach(cp => {
        ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#008000'; ctx.lineWidth = 1.5 / viewState.scale;
        ctx.beginPath(); ctx.arc(cp.x, cp.y, 4 / viewState.scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      });
    }
    ctx.restore();
  }, [currentState, viewState, activeTool, lineWidth]);

  const resetView = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (container && canvas && image.naturalWidth > 0) {
      const { width: cW, height: cH } = container.getBoundingClientRect();
      canvas.width = cW; canvas.height = cH;
      const { naturalWidth: iW, naturalHeight: iH } = image;
      const scale = Math.min(cW / iW, cH / iH) * 0.9;
      setViewState({ scale, x: (cW - iW * scale) / 2, y: (cH - iH * scale) / 2 });
    }
  }, []);

  useEffect(() => {
    const image = imageRef.current;
    image.crossOrigin = "Anonymous"; image.src = imageUrl;
    image.onload = () => {
      resetView();
      if (initialPath && image.naturalWidth > 0) {
        const unnormalizedPath = initialPath.map(p => ({ x: p.x * image.naturalWidth, y: p.y * image.naturalHeight }));
        setActiveTool('freehand');
        updateCurrentState({ path: unnormalizedPath, controlPoints: [] });
      }
    };
    window.addEventListener('resize', resetView);
    return () => window.removeEventListener('resize', resetView);
  }, [imageUrl, resetView, initialPath]);

  useEffect(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(draw);
  }, [draw]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        setIsSpacePressed(true);
        if (activeTool !== 'pan') previousToolRef.current = activeTool;
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [activeTool]);

  const getWorldMousePos = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current; if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX - rect.left - viewState.x) / viewState.scale, y: (e.clientY - rect.top - viewState.y) / viewState.scale };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const worldPos = getWorldMousePos(e);
    if (finalActiveTool === 'pan') {
      setIsDrawing(true); setStartPanPoint({ x: e.clientX - viewState.x, y: e.clientY - viewState.y });
    } else if (activeTool === 'points') {
      for (let i = currentState.controlPoints.length - 1; i >= 0; i--) {
        if (Math.hypot(currentState.controlPoints[i].x - worldPos.x, currentState.controlPoints[i].y - worldPos.y) < 6 / viewState.scale) {
          setDraggingPoint({ type: 'control', index: i }); setIsDrawing(true); return;
        }
      }
      for (let i = currentState.path.length - 1; i >= 0; i--) {
        if (Math.hypot(currentState.path[i].x - worldPos.x, currentState.path[i].y - worldPos.y) < 6 / viewState.scale) {
          setDraggingPoint({ type: 'anchor', index: i }); setIsDrawing(true); return;
        }
      }
      updateCurrentState(s => {
        const newPath = [...s.path, worldPos];
        let newCp = [...s.controlPoints];
        if (newPath.length > 1) {
          const p1 = newPath[newPath.length - 2]; const p2 = newPath[newPath.length - 1];
          newCp.push({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
        }
        return { path: newPath, controlPoints: newCp };
      });
    } else if (activeTool === 'freehand') {
      setIsDrawing(true); updateCurrentState({ path: [worldPos], controlPoints: [] });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing) return;
    const worldPos = getWorldMousePos(e);
    if (finalActiveTool === 'pan') {
      setViewState(v => ({ ...v, x: e.clientX - startPanPoint.x, y: e.clientY - startPanPoint.y }));
    } else if (activeTool === 'points' && draggingPoint) {
      setCurrentState(s => {
        let newPath = [...s.path]; let newCp = [...s.controlPoints];
        if (draggingPoint.type === 'anchor') {
          newPath[draggingPoint.index] = worldPos;
          if (draggingPoint.index > 0) newCp[draggingPoint.index - 1] = { x: (newPath[draggingPoint.index - 1].x + worldPos.x) / 2, y: (newPath[draggingPoint.index - 1].y + worldPos.y) / 2 };
          if (draggingPoint.index < newPath.length - 1) newCp[draggingPoint.index] = { x: (worldPos.x + newPath[draggingPoint.index + 1].x) / 2, y: (worldPos.y + newPath[draggingPoint.index + 1].y) / 2 };
        } else { newCp[draggingPoint.index] = worldPos; }
        return { path: newPath, controlPoints: newCp };
      });
    } else if (activeTool === 'freehand') {
      setCurrentState(s => ({ ...s, path: [...s.path, worldPos] }));
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && activeTool !== 'pan' && finalActiveTool !== 'pan') {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(currentState);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
    setIsDrawing(false); setDraggingPoint(null);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault(); const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect(); const mouseX = e.clientX - rect.left; const mouseY = e.clientY - rect.top;
    const scaleAmount = 1.1; const delta = e.deltaY > 0 ? 1 / scaleAmount : scaleAmount;
    const newScale = Math.max(0.1, Math.min(viewState.scale * delta, 20));
    const worldX = (mouseX - viewState.x) / viewState.scale; const worldY = (mouseY - viewState.y) / viewState.scale;
    const newX = mouseX - worldX * newScale; const newY = mouseY - worldY * newScale;
    setViewState({ scale: newScale, x: newX, y: newY });
  };

  const handleClear = () => updateCurrentState({ path: [], controlPoints: [] });

  const sampleQuadraticBezier = (p0: Point, p1: Point, p2: Point, steps: number): Point[] => {
    const points: Point[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = Math.pow(1 - t, 2) * p0.x + 2 * (1 - t) * t * p1.x + Math.pow(t, 2) * p2.x;
      const y = Math.pow(1 - t, 2) * p0.y + 2 * (1 - t) * t * p1.y + Math.pow(t, 2) * p2.y;
      points.push({ x, y });
    }
    return points;
  };

  const handleConfirm = () => {
    const image = imageRef.current;
    if (image.naturalWidth === 0) { onConfirm([]); return; }
    let finalPath: Point[] = [];
    const { path, controlPoints } = currentState;

    if (activeTool === 'freehand') {
      finalPath = path;
    } else if (activeTool === 'points') {
      if (path.length < 2) { onConfirm([]); return; }
      finalPath.push(path[0]);
      for (let i = 0; i < path.length - 1; i++) {
        const p0 = path[i];
        const p1 = controlPoints[i] || { x: (p0.x + path[i + 1].x) / 2, y: (p0.y + path[i + 1].y) / 2 };
        const p2 = path[i + 1];
        finalPath.push(...sampleQuadraticBezier(p0, p1, p2, 20).slice(1));
      }
    }
    if (finalPath.length < 2) { onConfirm([]); return; }
    onConfirm(finalPath.map(p => ({ x: p.x / image.naturalWidth, y: p.y / image.naturalHeight })));
  };

  const toolConfig = {
    pan: { cursor: 'grab', icon: <HandIcon className="w-5 h-5" />, name: 'Pan (Spacebar)' },
    freehand: { cursor: 'crosshair', icon: <PenToolIcon className="w-5 h-5" />, name: 'Freehand Draw' },
    points: { cursor: 'crosshair', icon: <SplineIcon className="w-5 h-5" />, name: 'Points & Curves' },
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-zinc-900 text-white"
    >
      <header className="absolute top-0 left-0 right-0 z-30 p-4 flex justify-between items-center bg-gradient-to-b from-black/50 to-transparent">
        <h2 className="text-lg font-bold text-zinc-100">Gum Line Editor</h2>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-semibold text-zinc-300 bg-zinc-800/80 rounded-lg hover:bg-zinc-700 transition-colors">Cancel</button>
          <button onClick={handleConfirm} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
            <CheckCircleIcon className="w-5 h-5" /> Confirm Guide
          </button>
        </div>
      </header>

      <main ref={containerRef} className="w-full h-full" onWheel={handleWheel}>
        <canvas ref={canvasRef} className="w-full h-full"
          style={{ cursor: isDrawing && finalActiveTool === 'pan' ? 'grabbing' : toolConfig[finalActiveTool].cursor }}
          onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} />
      </main>

      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-2 p-2 rounded-full bg-zinc-900/80 backdrop-blur-xl border border-white/10 shadow-2xl">
        {(Object.keys(toolConfig) as Tool[]).map(tool => (
          <Tooltip key={tool} text={toolConfig[tool].name}>
            <button
              onClick={() => setActiveTool(tool)}
              className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-colors ${activeTool === tool ? 'text-teal-400' : 'text-zinc-300 hover:bg-white/10'}`}>
              {activeTool === tool && <motion.div layoutId="gum-tool-active" className="absolute inset-0 bg-white/10 rounded-full" />}
              <span className="relative z-10">{toolConfig[tool].icon}</span>
            </button>
          </Tooltip>
        ))}
      </div>

      <aside className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-64 p-2 rounded-xl bg-zinc-900/80 backdrop-blur-xl border border-white/10 shadow-2xl">
        <PanelSection title="View">
          <ControlSlider label="Zoom" value={viewState.scale} onChange={e => setViewState(v => ({ ...v, scale: Number(e.target.value) }))} min={0.1} max={10} step={0.05} onDoubleClick={resetView} />
          <button onClick={resetView} className="w-full mt-2 px-3 py-1.5 text-xs font-semibold text-zinc-300 bg-white/5 rounded-md hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
            <FitToScreenIcon className="w-4 h-4" /> Fit to Screen
          </button>
        </PanelSection>
        <PanelSection title="Path">
          <ControlSlider label="Line Width" value={lineWidth} onChange={e => setLineWidth(Number(e.target.value))} min={1} max={20} step={0.5} onDoubleClick={() => setLineWidth(4)} />
        </PanelSection>
        <PanelSection title="Actions">
          <div className="flex items-center justify-around">
            <button onClick={handleUndo} title="Undo" disabled={historyIndex === 0} className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 disabled:opacity-40 transition-colors"><UndoIcon className="w-5 h-5" /></button>
            <button onClick={handleRedo} title="Redo" disabled={historyIndex === history.length - 1} className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 disabled:opacity-40 transition-colors"><RedoIcon className="w-5 h-5" /></button>
            <button onClick={handleClear} title="Clear Path" disabled={currentState.path.length === 0} className="p-2.5 rounded-full text-red-400 hover:bg-red-500/20 disabled:opacity-40 transition-colors"><RefreshCwIcon className="w-5 h-5" /></button>
          </div>
        </PanelSection>
      </aside>
      <style>{`.range-slider{-webkit-appearance:none;appearance:none;width:100%;height:4px;background:rgba(255,255,255,0.1);border-radius:2px;outline:none;transition:all .2s}.range-slider:hover{background:rgba(255,255,255,0.2)}.range-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:14px;height:14px;background:#2dd4bf;cursor:pointer;border-radius:50%;border:2px solid #18181b;transition:transform .2s ease,box-shadow .2s ease}.range-slider:active::-webkit-slider-thumb,.range-slider::-webkit-slider-thumb:hover{transform:scale(1.2);box-shadow:0 0 8px #2dd4bf}.range-slider::-moz-range-thumb{width:14px;height:14px;background:#2dd4bf;cursor:pointer;border-radius:50%;border:2px solid #18181b;transition:transform .2s ease,box-shadow .2s ease}.range-slider:active::-moz-range-thumb,.range-slider::-moz-range-thumb:hover{transform:scale(1.2);box-shadow:0 0 8px #2dd4bf}`}</style>
    </motion.div>
  );
};

export default GumLineEditor;
